import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { requestOtp } from '../../lib/api/auth';
import { useFocusOnMount } from '../../lib/hooks/useFocusOnMount';
import { Button } from '../Button';
import { BackLink } from './BackLink';
import { FormError } from '../form/FormError';
import { FormField } from '../form/FormField';
import { RegisterLayout } from './RegisterLayout';

/**
 * Step 2 — email entry (Figma /reg-2).
 *
 * Calls POST /auth/request-otp and either advances to the code step on a
 * clean 200 or surfaces the typo-guard's 422 suggestion inline. The user
 * can accept the suggested email (resubmits with the corrected address) or
 * keep their original (resubmits with confirmedOriginal: true).
 *
 * Field-level errors live under the email input; non-field errors (rate
 * limit, network) render as a banner above the action row.
 */
type EmailStepProps = {
  initialEmail: string;
  onBack: () => void;
  /** Called with the email the API accepted (after typo-confirmation flow). */
  onAdvance: (email: string) => void;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailStep({ initialEmail, onBack, onAdvance }: EmailStepProps) {
  const [email, setEmail] = useState(initialEmail);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [typoSuggestion, setTypoSuggestion] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // Set true between a successful onAdvance call and this step unmounting via
  // AnimatePresence's exit fade — blocks any button on this step from being
  // re-tapped during the ~0.25s crossfade.
  const [transitioning, setTransitioning] = useState(false);
  const busy = pending || transitioning;
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  async function submit(args: { email: string; confirmedOriginal?: boolean }): Promise<void> {
    setFieldError(null);
    setBannerError(null);
    setPending(true);
    try {
      const result = await requestOtp(args.email, args.confirmedOriginal);
      if (result.kind === 'typo') {
        setTypoSuggestion(result.suggested);
        return;
      }
      setTransitioning(true);
      onAdvance(args.email);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setBannerError("You're trying that a bit too often. Take a breath and try again in a minute.");
      } else if (err instanceof ApiError && err.status >= 500) {
        setBannerError('Something went wrong on our end. Please try again.');
      } else if (err instanceof ApiError && err.status === 400) {
        setFieldError(err.message);
      } else {
        setBannerError('Network error. Check your connection and try again.');
      }
    } finally {
      setPending(false);
    }
  }

  function handleSubmit(): void {
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setFieldError('Please enter a valid email address.');
      return;
    }
    setTypoSuggestion(null);
    void submit({ email: trimmed });
  }

  return (
    <RegisterLayout>
      <BackLink onClick={onBack} disabled={busy} />
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-center font-['Orbitron',sans-serif] text-[clamp(16px,4.8vw,22px)] uppercase tracking-[0.1em] text-white focus:outline-none"
      >
        Please enter <br className="sm:hidden" /> your email
      </h2>

      <form
        className="flex flex-col items-stretch gap-[16px]"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        noValidate
      >
        <FormField
          label="Email address"
          visuallyHiddenLabel
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldError) setFieldError(null);
            if (typoSuggestion) setTypoSuggestion(null);
          }}
          error={fieldError}
          required
        />

        <p className="text-center font-['Arial',sans-serif] text-[14px] text-white/80">
          To confirm it&apos;s you, we&apos;ll send a one-time code to your email. You&apos;ll
          need to enter it to continue.
        </p>

        {typoSuggestion && (
          <div
            role="alert"
            className="flex flex-col gap-[10px] rounded-[12px] border border-[#00ffb2]/40 bg-[#00ffb2]/10 px-4 py-3 font-['Arial',sans-serif] text-[14px] text-white"
          >
            <p>
              Did you mean <span className="font-bold text-[#00ffb2]">{typoSuggestion}</span>?
            </p>
            <div className="flex flex-wrap gap-[10px]">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEmail(typoSuggestion);
                  void submit({ email: typoSuggestion });
                }}
                className="rounded-full bg-white px-4 py-2 font-['Arial',sans-serif] text-[13px] text-[#1f1a26] hover:bg-[#ebebeb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
              >
                Use suggestion
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void submit({ email: email.trim(), confirmedOriginal: true })}
                className="rounded-full border border-white/40 px-4 py-2 font-['Arial',sans-serif] text-[13px] text-white hover:border-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-white/40"
              >
                Keep original
              </button>
            </div>
          </div>
        )}

        <FormError>{bannerError}</FormError>

        <div className="flex flex-col items-center gap-[16px]">
          <Button variant="dark" type="submit" disabled={busy} className="min-w-[200px]">
            {pending ? 'Sending…' : 'Next'}
          </Button>
        </div>
      </form>
    </RegisterLayout>
  );
}
